/**
 * FikoCMMDialogComponent - Canlı Tarama Dialog
 *
 * Mobil kamera ile 3D tarama için minimal/kompakt dialog.
 * FikoYT dialog tarzına uygun tasarım.
 */

import { Component, Input, ChangeDetectorRef, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYTService } from '../../../../../../fikoyt.service';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoCMMService, ScanMode, ScanState, CMMStats, FikoCanlıTaramaGrup } from './FikoCMMService';
import { ConnectionState, PairingInfo } from './FikoRemoteCamera';

@Component({
    selector: 'fikoyt-cmm-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: `
<!-- CATIA Ultra Minimal Toolbar -->
<div style="display: flex; gap: 1px; background: #34495e; padding: 2px; border-radius: 2px;">
    <button class="ui icon mini button"
            style="padding: 0.2rem; background: transparent; border: none; margin: 0;"
            [style.background]="aktifSekme === 'baglanti' ? '#00E5FF' : 'transparent'"
            title="Bağlantı"
            (click)="aktifSekme='baglanti'; CD.detectChanges();">
        <i class="wifi icon" style="opacity: 0.9; font-size: 12px;"
           [style.color]="connectionState === 'connected' ? '#00FF00' : '#FFF'"></i>
    </button>

    <button class="ui icon mini button"
            style="padding: 0.2rem; background: transparent; border: none; margin: 0;"
            [style.background]="aktifSekme === 'tarama' ? '#00E5FF' : 'transparent'"
            [disabled]="connectionState !== 'connected'"
            title="Tarama"
            (click)="aktifSekme='tarama'; CD.detectChanges();">
        <i class="icon camera" style="opacity: 0.9; font-size: 12px;"></i>
    </button>

    <button class="ui icon mini button"
            style="padding: 0.2rem; background: transparent; border: none; margin: 0; margin-left: auto;"
            [style.background]="aktifSekme === 'ayarlar' ? '#00E5FF' : 'transparent'"
            title="Ayarlar"
            (click)="aktifSekme='ayarlar'; CD.detectChanges();">
        <i class="icon cog" style="opacity: 0.9; font-size: 12px;"></i>
    </button>
</div>

<div class="ui divider" style="margin: 0.2rem 0;"></div>

<!-- Video Önizleme (her zaman görünür, bağlı ise) -->
@if (connectionState === 'connected') {
<div style="background: #000; border-radius: 4px; overflow: hidden; margin-bottom: 0.5rem; position: relative;">
    <video #videoPreview autoplay playsinline muted
           style="width: 100%; height: 120px; object-fit: cover;"></video>
    @if (!videoStream) {
    <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; color: #666; font-size: 10px;">
        Video bekleniyor...
    </div>
    }
</div>
}

<!-- Bağlantı Sekmesi -->
@if (aktifSekme === 'baglanti') {
<div class="sekme-icerik">

    <!-- Bağlı Değil -->
    @if (connectionState === 'disconnected') {
    <div style="text-align: center; padding: 1rem;">
        <i class="massive mobile alternate icon" style="color: #666;"></i>
        <p style="margin: 0.5rem 0; color: #999; font-size: 11px;">
            Mobil cihaz bağlı değil
        </p>
        <button class="ui mini primary button" (click)="baglantiBaslat()">
            <i class="qrcode icon"></i> Eşleştir
        </button>
    </div>
    }

    <!-- Eşleştirme QR -->
    @if (connectionState === 'pairing' && pairingInfo) {
    <div style="text-align: center; padding: 0.5rem;">
        <p style="margin: 0; color: #00E5FF; font-size: 11px; font-weight: bold;">
            QR Kodu Telefonla Tarayın
        </p>
        <img [src]="pairingInfo.qrDataUrl"
             style="width: 150px; height: 150px; margin: 0.5rem 0; border: 2px solid #00E5FF; border-radius: 4px;">
        <p style="margin: 0; color: #666; font-size: 10px;">
            Kod: <strong style="color: #00E5FF;">{{ pairingInfo.pairingCode }}</strong>
        </p>
        <button class="ui mini button" style="margin-top: 0.5rem;" (click)="iptalEt()">
            İptal
        </button>
    </div>
    }

    <!-- Bağlanıyor -->
    @if (connectionState === 'connecting') {
    <div style="text-align: center; padding: 1rem;">
        <div class="ui active inline loader small"></div>
        <p style="margin: 0.5rem 0; color: #00E5FF; font-size: 11px;">
            Bağlanıyor...
        </p>
    </div>
    }

    <!-- Bağlı -->
    @if (connectionState === 'connected') {
    <div style="padding: 0.5rem;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 0.5rem;">
            <i class="mobile alternate icon" style="color: #00FF00; font-size: 18px;"></i>
            <div>
                <div style="color: #00FF00; font-size: 11px; font-weight: bold;">Bağlı</div>
                <div style="color: #666; font-size: 10px;">{{ cameraInfo?.deviceName || 'Mobil Kamera' }}</div>
            </div>
            <button class="ui mini red icon button" style="margin-left: auto;" (click)="baglantiKes()">
                <i class="close icon"></i>
            </button>
        </div>

        @if (cameraInfo) {
        <div style="font-size: 10px; color: #888;">
            <div>Çözünürlük: {{ cameraInfo.resolution.width }}x{{ cameraInfo.resolution.height }}</div>
            @if (cameraInfo.batteryLevel) {
            <div>Pil: {{ cameraInfo.batteryLevel }}%</div>
            }
        </div>
        }
    </div>
    }

    <!-- Point Cloud Import (Bağlantı sekmesinde de) -->
    <div class="ui divider" style="margin: 0.5rem 0;"></div>
    <div style="margin-bottom: 0.5rem;">
        <label style="font-size: 10px; color: #666; display: block; margin-bottom: 4px;">
            Point Cloud Yükle
        </label>
        <input type="file"
               #fileInputBaglanti
               [accept]="desteklenenFormatlar"
               (change)="dosyaYukle($event)"
               style="display: none;">
        <div style="display: flex; gap: 4px;">
            <button class="ui mini button" style="flex: 1; background: #2a2a4a; color: #00E5FF;" (click)="fileInputBaglanti.click()">
                <i class="upload icon"></i> Dosya Seç
            </button>
        </div>
        <div style="font-size: 9px; color: #555; margin-top: 4px;">
            PLY, XYZ, PCD, OBJ, PTS, ASC
        </div>
        @if (yuklemeDurumu) {
        <div style="font-size: 10px; margin-top: 4px;"
             [style.color]="yuklemeDurumu.includes('Hata') ? '#ff6b6b' : '#00E5FF'">
            {{ yuklemeDurumu }}
        </div>
        }
    </div>

</div>
}

<!-- Tarama Sekmesi -->
@if (aktifSekme === 'tarama') {
<div class="sekme-icerik">

    <!-- Tarama Durumu -->
    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
        <span style="font-size: 11px; color: #999;">
            @switch (scanState) {
                @case ('idle') { Hazır }
                @case ('connected') { Bağlı }
                @case ('scanning') { <span style="color: #00FF00;">Taranıyor...</span> }
                @case ('processing') { <span style="color: #FFD700;">İşleniyor...</span> }
                @case ('completed') { <span style="color: #00E5FF;">Tamamlandı</span> }
            }
        </span>

        @if (scanState === 'scanning') {
        <span style="font-size: 10px; color: #00E5FF;">
            {{ stats.framesCaptured }} kare
        </span>
        }
    </div>

    <!-- Tarama Adı -->
    <div style="margin-bottom: 0.5rem;">
        <input type="text"
               [(ngModel)]="taramaAdi"
               placeholder="Tarama adı..."
               style="width: 100%; padding: 4px 8px; font-size: 11px; background: #2a2a2a; border: 1px solid #444; color: #fff; border-radius: 2px;">
    </div>

    <!-- Kontrol Butonları -->
    <div style="display: flex; gap: 4px;">
        @if (scanState === 'idle' || scanState === 'connected' || scanState === 'completed') {
        <button class="ui mini green button" style="flex: 1;" (click)="taramaBaslat()">
            <i class="play icon"></i> Başlat
        </button>
        }

        @if (scanState === 'scanning') {
        <button class="ui mini yellow button" style="flex: 1;" (click)="taramaDurdur()">
            <i class="pause icon"></i> Durdur
        </button>
        <button class="ui mini blue button" style="flex: 1;" (click)="taramaTamamla()">
            <i class="check icon"></i> Bitir
        </button>
        }
    </div>

    <!-- İstatistikler -->
    @if (scanState === 'scanning' || scanState === 'completed') {
    <div class="ui divider" style="margin: 0.5rem 0;"></div>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 10px;">
        <div style="background: #2a2a2a; padding: 4px; border-radius: 2px; text-align: center;">
            <div style="color: #00E5FF; font-weight: bold;">{{ stats.pointsCollected }}</div>
            <div style="color: #666;">Nokta</div>
        </div>
        <div style="background: #2a2a2a; padding: 4px; border-radius: 2px; text-align: center;">
            <div style="color: #00E5FF; font-weight: bold;">{{ (stats.scanDuration / 1000).toFixed(1) }}s</div>
            <div style="color: #666;">Süre</div>
        </div>
    </div>
    }

    <!-- Point Cloud Import -->
    <div class="ui divider" style="margin: 0.5rem 0;"></div>
    <div style="margin-bottom: 0.5rem;">
        <label style="font-size: 10px; color: #666; display: block; margin-bottom: 4px;">
            Point Cloud Yükle
        </label>
        <input type="file"
               #fileInput
               [accept]="desteklenenFormatlar"
               (change)="dosyaYukle($event)"
               style="display: none;">
        <div style="display: flex; gap: 4px;">
            <button class="ui mini button" style="flex: 1; background: #2a2a4a; color: #00E5FF;" (click)="fileInput.click()">
                <i class="upload icon"></i> Dosya Seç
            </button>
        </div>
        <div style="font-size: 9px; color: #555; margin-top: 4px;">
            PLY, XYZ, PCD, OBJ, PTS, ASC
        </div>
        @if (yuklemeDurumu) {
        <div style="font-size: 10px; margin-top: 4px;"
             [style.color]="yuklemeDurumu.includes('Hata') ? '#ff6b6b' : '#00E5FF'">
            {{ yuklemeDurumu }}
        </div>
        }
    </div>

</div>
}

<!-- Ayarlar Sekmesi -->
@if (aktifSekme === 'ayarlar') {
<div class="sekme-icerik">

    <!-- Tarama Modu -->
    <div style="margin-bottom: 0.5rem;">
        <label style="font-size: 10px; color: #666;">Mod</label>
        <select [(ngModel)]="ayarlar.mode"
                style="width: 100%; padding: 4px; font-size: 11px; background: #2a2a2a; border: 1px solid #444; color: #fff; border-radius: 2px;">
            <option value="marker">Marker Tabanlı</option>
            <option value="markerless">Markersız</option>
            <option value="photogrammetry">Fotogrametri</option>
        </select>
    </div>

    <!-- Voxel Boyutu -->
    <div style="margin-bottom: 0.5rem;">
        <label style="font-size: 10px; color: #666;">Voxel (mm): {{ ayarlar.voxelSize }}</label>
        <input type="range" [(ngModel)]="ayarlar.voxelSize" min="0.5" max="10" step="0.5"
               style="width: 100%;">
    </div>

    <!-- Marker Boyutu -->
    @if (ayarlar.mode === 'marker') {
    <div style="margin-bottom: 0.5rem;">
        <label style="font-size: 10px; color: #666;">Marker (mm): {{ ayarlar.markerSize }}</label>
        <input type="range" [(ngModel)]="ayarlar.markerSize" min="20" max="200" step="10"
               style="width: 100%;">
    </div>
    <div style="margin-bottom: 0.5rem;">
        <button class="ui mini fluid button" style="background: #2a2a4a; color: #00E5FF;" (click)="markerYazdir()">
            <i class="print icon"></i> ArUco Marker Yazdır
        </button>
    </div>
    }

    <!-- Otomatik Yakalama -->
    <div style="display: flex; align-items: center; gap: 8px;">
        <input type="checkbox" id="autoCapture" [(ngModel)]="ayarlar.autoCapture">
        <label for="autoCapture" style="font-size: 11px; color: #999;">Otomatik Yakalama</label>
    </div>

</div>
}

<div class="ui divider" style="margin: 0.3rem 0;"></div>

<!-- Kapat Butonu -->
<div style="display: flex; justify-content: flex-end;">
    <button class="ui mini button" (click)="kapa()">Kapat</button>
</div>
    `,
    styles: [`
        :host {
            display: block;
            min-width: 250px;
            font-family: 'Segoe UI', sans-serif;
        }

        .sekme-icerik {
            min-height: 120px;
        }

        .ui.button {
            font-size: 11px !important;
        }

        .ui.divider {
            border-color: #444 !important;
        }
    `]
})
export class FikoCMMDialogComponent implements OnInit, OnDestroy {
    @Input() anaGosterim: FikoThreeJsComponent;
    @Input() pencere: any;
    @Input() parametreler: any = {};

    @ViewChild('videoPreview') videoPreviewRef: ElementRef<HTMLVideoElement> | undefined;

    // Servis
    private cmmService: FikoCMMService;
    private subscriptions: Subscription[] = [];

    // UI State
    aktifSekme: 'baglanti' | 'tarama' | 'ayarlar' = 'baglanti';
    taramaAdi: string = 'Canlı Tarama';

    // Bağlantı durumu
    connectionState: ConnectionState = 'disconnected';
    pairingInfo: PairingInfo | null = null;
    cameraInfo: any = null;
    videoStream: MediaStream | null = null;

    // Tarama durumu
    scanState: ScanState = 'idle';
    stats: CMMStats = {
        framesCaptured: 0,
        pointsCollected: 0,
        markersDetected: 0,
        scanDuration: 0,
        lastFrameTime: 0
    };

    // Ayarlar
    ayarlar = {
        mode: 'marker' as ScanMode,
        voxelSize: 2,
        markerSize: 50,
        autoCapture: true
    };

    // Point Cloud Import
    desteklenenFormatlar = FikoCMMService.SUPPORTED_FORMATS.join(',');
    yuklemeDurumu: string = '';

    constructor(
        public readonly FikoYT: FikoYTService,
        public readonly CD: ChangeDetectorRef
    ) {
        this.cmmService = new FikoCMMService();
    }

    ngOnInit(): void {
        // Parametreleri işle
        if (this.parametreler?.anaGosterim) {
            this.anaGosterim = this.parametreler.anaGosterim;
        }

        // Sahneyi servishe bildir
        if (this.anaGosterim?.sahneler?.[0]) {
            this.cmmService.setScene(this.anaGosterim.sahneler[0]);
        }

        // Subscriptions
        this.subscriptions.push(
            this.cmmService.connectionState$.subscribe(state => {
                this.connectionState = state;
                if (state === 'connected') {
                    this.aktifSekme = 'tarama';
                }
                this.CD.detectChanges();
            }),

            this.cmmService.pairingInfo$.subscribe(info => {
                this.pairingInfo = info;
                this.CD.detectChanges();
            }),

            this.cmmService.state$.subscribe(state => {
                this.scanState = state;
                this.CD.detectChanges();
            }),

            this.cmmService.stats$.subscribe(stats => {
                this.stats = stats;
                this.CD.detectChanges();
            }),

            // Video stream (PC'de önizleme)
            this.cmmService.videoStream$.subscribe(stream => {
                // console.log('[CMM Dialog] Video stream geldi:', stream ? 'var' : 'yok');
                this.videoStream = stream;
                this.CD.detectChanges();

                // Video element'e bağla
                setTimeout(() => {
                    this.attachVideoStream(stream);
                }, 100);
            }),

            // Kamera bilgisi
            this.cmmService.cameraInfo$.subscribe(info => {
                this.cameraInfo = info;
                this.CD.detectChanges();
            })
        );

        // Dialog close event
        if (this.pencere?.onDialogClose) {
            this.subscriptions.push(
                this.pencere.onDialogClose.subscribe(() => {
                    this.temizle();
                })
            );
        }
    }

    /**
     * Bağlantı başlat (QR oluştur)
     */
    async baglantiBaslat(): Promise<void> {
        try {
            await this.cmmService.startPairing();
        } catch (error: any) {
            console.error('[CMM Dialog] Eşleştirme hatası:', error);
        }
    }

    /**
     * Bağlantıyı kes
     */
    baglantiKes(): void {
        this.cmmService.disconnect();
    }

    /**
     * İptal et
     */
    iptalEt(): void {
        this.cmmService.disconnect();
    }

    /**
     * Tarama başlat
     */
    taramaBaslat(): void {
        const grup = this.cmmService.startScan(this.taramaAdi || 'Canlı Tarama');
        if (grup) {
            // Ürün ağacını güncelle
            this.anaGosterim?.ciz();
        }
    }

    /**
     * Tarama durdur
     */
    taramaDurdur(): void {
        this.cmmService.stopScan();
    }

    /**
     * Tarama tamamla
     */
    async taramaTamamla(): Promise<void> {
        const result = await this.cmmService.completeScan();
        if (result) {
            // console.log('[CMM Dialog] Tarama tamamlandı:', result.name);
            this.anaGosterim?.ciz();
        }
    }

    /**
     * Dialog'ı kapat
     */
    kapa(): void {
        this.temizle();
        if (this.pencere?.kapa) {
            this.pencere.kapa();
        }
    }

    /**
     * ArUco marker yazdırma sayfasını aç
     */
    markerYazdir(): void {
        window.open('/aruco-print', '_blank');
    }

    /**
     * Point cloud dosyası yükle
     */
    async dosyaYukle(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) return;

        this.yuklemeDurumu = `Yükleniyor: ${file.name}...`;
        this.CD.detectChanges();

        try {
            // Sahneyi kontrol et ve set et
            const sahne = this.anaGosterim?.sahneler?.[0];
            if (sahne) {
                this.cmmService.setScene(sahne);
            }

            const grup = await this.cmmService.importPointCloud(file);

            if (grup) {
                // Ürün ağacına ekle (3D çizimler grubuna)
                if (sahne && !grup.parent) {
                    const urunAgaci = (sahne as any).urunAgaci;
                    if (urunAgaci) {
                        // "Fiko Çizimler" grubunu bul
                        let cizimGrup = urunAgaci.children?.find((c: any) => c.name === 'Fiko Çizimler');
                        if (cizimGrup) {
                            cizimGrup.add(grup);
                            // console.log('[CMM Dialog] Grup "Fiko Çizimler"e eklendi:', grup.name);
                        } else {
                            urunAgaci.add(grup);
                            // console.log('[CMM Dialog] Grup ürün ağacına eklendi:', grup.name);
                        }
                    } else {
                        sahne.add(grup);
                        // console.log('[CMM Dialog] Grup sahneye eklendi (ürün ağacı yok):', grup.name);
                    }
                }

                this.yuklemeDurumu = `✓ ${file.name} yüklendi`;

                // Render güncelle
                this.anaGosterim?.ciz();

                // Kameraya odaklan
                if (grup.children[0]) {
                    const points = grup.children[0] as THREE.Points;
                    if (points.geometry?.boundingSphere) {
                        // Kamerayı point cloud'a odakla
                        const sphere = points.geometry.boundingSphere;
                        const controls = (this.anaGosterim as any)?.secililer?.controls;
                        if (controls?.target) {
                            controls.target.copy(sphere.center);
                            controls.update?.();
                        }
                    }
                }
            } else {
                this.yuklemeDurumu = 'Hata: Dosya yüklenemedi';
            }
        } catch (error: any) {
            this.yuklemeDurumu = `Hata: ${error.message}`;
            console.error('[CMM Dialog] Import hatası:', error);
        }

        // Input'u temizle (aynı dosya tekrar seçilebilsin)
        input.value = '';
        this.CD.detectChanges();

        // 5 saniye sonra durumu temizle
        setTimeout(() => {
            if (!this.yuklemeDurumu.includes('Hata')) {
                this.yuklemeDurumu = '';
                this.CD.detectChanges();
            }
        }, 5000);
    }

    /**
     * Video stream'i element'e bağla
     */
    private attachVideoStream(stream: MediaStream | null): void {
        const videoEl = this.videoPreviewRef?.nativeElement;
        if (!videoEl) {
            // console.log('[CMM Dialog] Video element bulunamadı');
            return;
        }

        if (!stream) {
            videoEl.srcObject = null;
            // console.log('[CMM Dialog] Stream null, video temizlendi');
            return;
        }

        // Stream'i bağla
        videoEl.srcObject = stream;

        // Track durumunu kontrol et
        const videoTracks = stream.getVideoTracks();
        // console.log('[CMM Dialog] Video tracks:', videoTracks.length, videoTracks.map(t => ({
        //     enabled: t.enabled,
        //     muted: t.muted,
        //     readyState: t.readyState
        // })));

        // Explicit play çağrısı
        videoEl.play().then(() => {
            // console.log('[CMM Dialog] Video oynatma başladı');
        }).catch((err: any) => {
            console.error('[CMM Dialog] Video oynatma hatası:', err);
            // Muted olarak tekrar dene
            videoEl.muted = true;
            videoEl.play().catch((e: any) => {
                console.error('[CMM Dialog] Muted video da çalışmadı:', e);
            });
        });
    }

    /**
     * Temizle
     */
    private temizle(): void {
        this.cmmService.disconnect();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.cmmService.dispose();
    }
}
